<template>
  <RegisterPage
    :canSubmit="canSubmit"
    :error="error"
    @submit="handleCompanySelect"
    titleText="You can sign-up to Stream in less than five minutes"
  >
    <template #title>
      <h1>Find your business on companies house</h1>
    </template>
    <template #component>
      <div class="register-company">
        <AppSearchDropdown
          v-model="state.state.companyName"
          :options="companiesNames"
          :validate-immediate="!!state.state.companyName"
          label="Company name"
          rules="required|min:3"
        />
      </div>
    </template>
  </RegisterPage>
</template>

<script setup>
import debounce from 'lodash/debounce';
import { ref, computed, watch, onMounted } from 'vue';
import ApiClient from '@/ApiClient';
import AppSearchDropdown from '@/components/AppSearchDropdown';
import RegisterPage from '@/pages/register/components/RegisterPage';
import { RegistrationState } from '@/pages/register/register.js';
import useAnalytics from '@/state/composables/useAnalytics';
import { getUtmParams } from '@/utils';

const { postAnalyticsEvent } = useAnalytics();

const emit = defineEmits(['continue']);

const state = ref(new RegistrationState().load());
const companies = ref([]);
const error = ref(false);

const companiesNames = computed(() => companies.value.map(i => i.title));
const canSubmit = computed(() => companiesNames.value.includes(state.value.state.companyName));

watch(
  () => state.value.state.companyName,
  () => {
    if (state.value.state.companyName === '') {
      companies.value = [];
    } else {
      debouncedSearch(state.value.state.companyName);
    }
  },
);

const searchCompaniesHouse = async name => {
  if (name.length < 3) {
    companies.value = [];
    return;
  }

  try {
    const response = await ApiClient.smeCompanySearch(name);
    companies.value = response.items;
    error.value = false;
  } catch (e) {
    error.value = e?.message || 'Unable to search for company, please try again later';
  }
};

const debouncedSearch = debounce(name => {
  searchCompaniesHouse(name);
}, 200);

const handleCompanySelect = () => {
  state.value.state.companyNumber = companies.value.find(c => c.title === state.value.state.companyName).company_number;
  emit('continue', state.value.state.companyNumber);
  try {
    state.value.save();
    postAnalyticsEvent({
      intent: 'complete_self_serve_registration_company_details',
      task: 'add_company',
      action: 'click',
      ...getUtmParams(),
    });
    state.value.nextStep();
  } catch {
    undefined;
  }
};

onMounted(() => {
  postAnalyticsEvent({
    intent: 'view_self_serve_registration_company_details',
    task: 'add_company',
    action: 'view',
    ...getUtmParams(),
  });
  if (state.value.state.companyName !== '') {
    debouncedSearch(state.value.state.companyName);
  }
});
</script>

<style scoped lang="scss">
.register-company {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

@media (max-width: 900px) {
  .register-company {
    justify-content: start;
  }
}
</style>
